import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import { toOrganisation } from "@/queries/organisations"
import type { ApiPage, ApiResource, Paginated } from "@/lib/api/types"
import type {
  CreateOrganisationInput,
  ApiOrganisation,
  Organisation,
  OrganisationFilters,
  UpdateOrganisationInput,
} from "@/types/organisation"

export const adminOrganisationQueries = {
  list: (filters: OrganisationFilters = {}) =>
    queryOptions({
      queryKey: apiKey("/system/organisations", filters),
      queryFn: async ({ signal }) => {
        const response = await apiFetch<ApiPage<ApiOrganisation>>(
          "/system/organisations",
          {
            params: {
              search: filters.search,
              page: filters.page,
              per_page: filters.perPage,
            },
            signal,
          }
        )

        return {
          items: response.data.map(toOrganisation),
          total: response.meta.total,
          currentPage: response.meta.current_page,
          lastPage: response.meta.last_page,
        } satisfies Paginated<Organisation>
      },
    }),

  detail: (organisationSlug: string) =>
    queryOptions({
      queryKey: apiKey(`/system/organisations/${organisationSlug}`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiOrganisation>>(
          `/system/organisations/${organisationSlug}`,
          { signal }
        )

        return toOrganisation(data)
      },
    }),
}

export function useCreateOrganisation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateOrganisationInput) => {
      const { data } = await apiFetch<ApiResource<ApiOrganisation>>(
        "/system/organisations",
        {
          method: "POST",
          body: {
            name: input.name,
            id: input.id,
            admin_name: input.adminName,
            admin_email: input.adminEmail,
            fca_reference: input.fcaReference,
            company_number: input.companyNumber,
          },
        }
      )

      return toOrganisation(data)
    },
    onSuccess: (organisation) => {
      queryClient.setQueryData(
        apiKey(`/system/organisations/${organisation.id}`),
        organisation
      )
    },
  })
}

export function useUpdateOrganisation(organisationSlug: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateOrganisationInput) => {
      const { data } = await apiFetch<ApiResource<ApiOrganisation>>(
        `/system/organisations/${organisationSlug}`,
        {
          method: "PATCH",
          body: {
            name: input.name,
            id: input.id,
            status: input.status,
            fca_reference: input.fcaReference,
            company_number: input.companyNumber,
          },
        }
      )

      return toOrganisation(data)
    },
    onSuccess: (organisation) => {
      queryClient.setQueryData(
        apiKey(`/system/organisations/${organisation.id}`),
        organisation
      )
    },
  })
}
